import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Camera, ImagePlus, Loader2, Package, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";

const getPos = () => new Promise((res) => {
  if (!navigator.geolocation) return res({});
  navigator.geolocation.getCurrentPosition((p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }), () => res({}), { timeout: 2500, maximumAge: 60000 });
});

// "Picked up" = the action of loading the item; result = status On Board. Prompts for an optional photo + info.
export function PickupDialog({ job, onChange }) {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const camRef = useRef(null);
  const galRef = useRef(null);
  const addFiles = (e) => setFiles((f) => [...f, ...Array.from(e.target.files || [])]);

  const submit = async () => {
    setBusy(true);
    try {
      const pos = await getPos();
      await api.post(`/driver/jobs/${job.id}/status`, { status: "picked_up", note: notes ? `Picked up — ${notes}` : "Picked up — on board", ...pos });
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f); fd.append("kind", "package");
        try { await api.post(`/orders/${job.id}/photos`, fd); } catch { toast.error(`Couldn't upload ${f.name}`); }
      }
      toast.success(`${job.tracking_number} is on board — customer notified`);
      setOpen(false); setFiles([]); setNotes(""); onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="lg" data-testid={`pickup-button-${job.tracking_number}`} className="h-12 flex-1 gap-2 bg-kilted-green hover:bg-kilted-greenLight">
          <Package className="h-4 w-4" /> Picked up
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="pickup-dialog">
        <DialogHeader>
          <DialogTitle>Picked up — {job.tracking_number}</DialogTitle>
          <DialogDescription>Item goes on board. Snap a photo of the parcel and add anything worth noting (optional).</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Photo of the item</Label>
            <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={addFiles} data-testid="pickup-photo-camera-input" />
            <input ref={galRef} type="file" accept="image/*" multiple className="hidden" onChange={addFiles} data-testid="pickup-photo-input" />
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => camRef.current?.click()} data-testid="pickup-take-photo" className="flex-1 gap-2 border-kilted-green text-kilted-green hover:bg-kilted-green/10"><Camera className="h-4 w-4" /> Take photo</Button>
              <Button type="button" variant="outline" onClick={() => galRef.current?.click()} data-testid="pickup-upload-photo" className="flex-1 gap-2 border-kilted-green text-kilted-green hover:bg-kilted-green/10"><ImagePlus className="h-4 w-4" /> Upload</Button>
            </div>
            {files.length > 0 && <p className="text-xs text-emerald-700" data-testid="pickup-photo-count">{files.length} photo{files.length === 1 ? "" : "s"} ready</p>}
          </div>
          <div className="space-y-1.5"><Label>Info / condition</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="e.g. Box slightly dented on arrival · 3 cartons taped together" data-testid="pickup-notes-input" /></div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} data-testid="pickup-submit-button" className="gap-2 bg-kilted-green hover:bg-kilted-greenLight">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />} Confirm on board
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Hand the job to another courier's queue; the original driver keeps a read-only shell.
export function TransferDialog({ job, onChange }) {
  const [open, setOpen] = useState(false);
  const [colleagues, setColleagues] = useState([]);
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) api.get("/driver/colleagues").then((r) => setColleagues(r.data)).catch(() => setColleagues([])); }, [open]);

  const submit = async () => {
    if (!to) { toast.error("Pick the courier to hand this to"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/driver/jobs/${job.id}/transfer`, { to_driver_id: to, note });
      toast.success(`${job.tracking_number} transferred to ${data.driver_name}`);
      setOpen(false); onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="lg" variant="outline" data-testid={`transfer-button-${job.tracking_number}`} className="h-12 gap-2 border-kilted-green text-kilted-green hover:bg-kilted-green/10">
          <ArrowRightLeft className="h-4 w-4" /> Transfer
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="transfer-dialog">
        <DialogHeader>
          <DialogTitle>Transfer — {job.tracking_number}</DialogTitle>
          <DialogDescription>Moves this job to another courier's queue. You'll keep a greyed copy showing what you did.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Hand to</Label>
            <Select value={to} onValueChange={setTo}>
              <SelectTrigger data-testid="transfer-driver-select"><SelectValue placeholder="Choose a courier" /></SelectTrigger>
              <SelectContent>{colleagues.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`transfer-driver-${c.id}`}>{c.name}{c.vehicle ? ` — ${c.vehicle}` : ""}</SelectItem>)}</SelectContent>
            </Select>
            {colleagues.length === 0 && <p className="text-xs text-muted-foreground">No other couriers available.</p>}
          </div>
          <div className="space-y-1.5"><Label>Note for them</Label><Textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 300))} rows={2} placeholder="e.g. In the back of the HiLux, left side · customer expects it before 3pm" data-testid="transfer-note-input" /></div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy || !to} data-testid="transfer-submit-button" className="gap-2 bg-kilted-green hover:bg-kilted-greenLight">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRightLeft className="h-4 w-4" />} Confirm transfer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
